<template>
  <Transition name="toast">
    <div
      v-if="visible"
      class="flex items-center gap-2 px-4 py-3 rounded-lg shadow-lg border min-w-[280px] max-w-[400px]"
      :class="toastClasses"
    >
      <Icon
        :icon="iconName"
        class="w-5 h-5 shrink-0"
      />
      <span class="text-sm font-medium flex-1">{{ toast.message }}</span>
      <button
        class="shrink-0 p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
        @click="handleClose"
      >
        <Icon
          icon="heroicons:x-mark"
          class="w-4 h-4"
        />
      </button>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { Icon } from '@iconify/vue'
import type { Toast, ToastType } from '@/types/toast'

const props = defineProps<{
  toast: Toast
}>()

const emit = defineEmits<{
  close: [id: number]
}>()

const visible = ref(false)

const toastClasses = computed(() => {
  const classes: Record<ToastType, string> = {
    success: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-800 dark:text-green-300',
    error: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-800 dark:text-red-300',
    info: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300',
    warning: 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-800 dark:text-amber-300'
  }
  return classes[props.toast.type]
})

const iconName = computed(() => {
  const icons: Record<ToastType, string> = {
    success: 'heroicons:check-circle-solid',
    error: 'heroicons:x-circle-solid',
    info: 'heroicons:information-circle-solid',
    warning: 'heroicons:exclamation-triangle-solid'
  }
  return icons[props.toast.type]
})

function handleClose() {
  visible.value = false
  setTimeout(() => {
    emit('close', props.toast.id)
  }, 200)
}

onMounted(() => {
  requestAnimationFrame(() => {
    visible.value = true
  })
})
</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.2s ease;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>
